import { sideEditorContext } from '@context/side-editor-context';
import type { TabsOrientation } from '@reach/tabs';
import { useContext, useMemo } from 'react';

import {
  type NodeRegistrationEntryContent,
  type SideEditorScrollType,
  sideEditorPanelContext,
  sideEditorPanelsContainerContext,
} from './SideEditor';

/**
 * function that takes an html node to observe the visibility and calls the callback function when the node is visible
 * This function is needed because reach ui tab panel is hidden when it is not active
 * @param panelNodeToObserve The html node
 * @param callback The callback function
 */
function triggerWhenPanelIsVisible(panelNodeToObserve: HTMLDivElement, callback: () => void): void {
  const panelNodeObserver = new MutationObserver(function () {
    if (!panelNodeToObserve.hidden) {
      setTimeout(() => {
        callback();
        panelNodeObserver.disconnect();
      }, 200);
    }
  });

  panelNodeObserver.observe(panelNodeToObserve, {
    attributeFilter: ['hidden'],
    attributeOldValue: true,
  });
}

/**
 * function that takes an html node to observe the visibility and calls the callback function when the node is invisible
 * This function is needed because reach ui tab panel is hidden when it is not active
 * @param panelNodeToObserve The html node
 * @param callback The callback function
 */
function triggerWhenPanelIsInvisible(panelNodeToObserve: HTMLDivElement, callback: () => void): void {
  const panelNodeObserver = new MutationObserver(function () {
    if (panelNodeToObserve.hidden) {
      callback();
      panelNodeObserver.disconnect();
    }
  });

  panelNodeObserver.observe(panelNodeToObserve, {
    attributeFilter: ['hidden'],
    attributeOldValue: true,
  });
}

type SideEditorContextValues = {
  panelIndex: number;
  activeTab: number;
  changeTab: (newTab: number) => void;
  sideEditorPanelsNode: HTMLDivElement | undefined;
  registerNode: (key: string, nodeEntry: NodeRegistrationEntryContent) => void;
  scrollIntoView: (
    nodeRegistrationKey: string,
    scrollType?: SideEditorScrollType,
    compensateHeightToScroll?: boolean,
  ) => void;
  orientation: TabsOrientation;
};

/**
 * Custom hook that provides some useful attributes and functions related to the side editor
 */
function useSideEditorContext(): SideEditorContextValues {
  const { panelIndex } = useContext(sideEditorPanelContext);
  const { sideEditorPanelsNode, scrollIntoView, registerNode } = useContext(sideEditorPanelsContainerContext);
  const { activeTab, changeTab, orientation } = useContext(sideEditorContext);

  return useMemo(
    () => ({
      orientation,
      registerNode,
      panelIndex,
      sideEditorPanelsNode,
      activeTab,
      changeTab,
      scrollIntoView,
    }),
    [orientation, registerNode, panelIndex, sideEditorPanelsNode, activeTab, changeTab, scrollIntoView],
  );
}

export { triggerWhenPanelIsInvisible, triggerWhenPanelIsVisible, useSideEditorContext };
